import React, { useEffect, useState } from 'react';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { PulseLoader } from 'react-spinners';
import { createStructuredSelector } from 'reselect';
import { useErrorAlert } from '../../../../../components/error-alert/error-alert';
import Pagination from '../../../../../components/pagination/Pagination';
import { selectUser } from '../../../../../redux/user/user-selector';
import TaskTypesTableHeader from './components/task-types-table-header';
import TaskTypesTableRow from './components/task-types-table-row';
import './task-types-table.scss';
import { fetchTaskTypes, sortTasks } from './utils/task-types-table-util';
import AdminActionsPopup from '../../admin-actions-popup/AdminActionsPopup';
import { TriyoButton, PrimaryButton, SecondaryButton } from '../../../../../components/TriyoButton';
import PropTypes from 'prop-types';
import { GetNewTaskTypeSettings } from '../../../../../services/api/task-types-api';

const adminTaskTypeTableArea = 'admin-task-type-table-area';

const TaskTypesTable = ({ user }) => {

    const canCreate = user?.privileges['Task Types']?.Add;
    const canArchive = user?.privileges['Task Types']?.Delete;

    const [taskTypes, setTaskTypes] = useState([]);
    const [sortedTaskTypes, setSortedTaskTypes] = useState(taskTypes);
    const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);
    const [activeSortType, setActiveSortType] = useState('');
    const [isSortDescending, setIsSortDescending] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [showAdminActionsPopup, setShowAdminActionsPopup] = useState(false);
    const { alertError, setAlertError, ErrorAlert, errorAlertProps } = useErrorAlert();
    const { promiseInProgress } = usePromiseTracker({ area: adminTaskTypeTableArea });
    const history = useHistory();

    const rowProps = {
        canArchive,
        selectedTaskTypes,
        setSelectedTaskTypes,
        refreshTaskTypes,
        user
    };

    const headerProps = {
        canArchive,
        taskTypes,
        selectedTaskTypes,
        setSelectedTaskTypes,
        activeSortType,
        setActiveSortType,
        isSortDescending,
        setIsSortDescending,
        user
    };

    const paginationProps = {
        refresh: refreshTaskTypes,
        next() {
            if (pageNumber * pageSize > taskTypes.length) return;
            setPageNumber(pageNumber + 1);
        },
        prev() {
            if (pageNumber === 1) return;
            setPageNumber(pageNumber - 1);
        },
        rowChange: setPageSize,
        numEntries: taskTypes.length,
        query: {
            pageNumber,
            pageSize,
        },
    };

    const adminActionsPopupProps = {
        showAdminActionsPopup,
        setShowAdminActionsPopup,
        type: 'task type',
        name: selectedTaskTypes.length === 1 && getSelectedName(selectedTaskTypes[0]),
        selectedIDs: selectedTaskTypes,
        refreshTaskTypes,
    };

    useEffect(() => {
        try {
            refreshTaskTypes();
        } catch (error) {
            setAlertError({
                alertAction: 'Error Loading Task Types',
                alertMessage: error.toString(),
            });
        }
    }, []);

    useEffect(() => {
        setSortedTaskTypes(
            sortTasks({ taskTypes, sortBy: activeSortType, isSortDescending, pageNumber, pageSize })
        );
    }, [taskTypes, activeSortType, isSortDescending, pageNumber, pageSize]);

    async function refreshTaskTypes() {
        setSelectedTaskTypes([]);
        setPageNumber(1);
        setPageSize(10);
        const taskTypes = await trackPromise(fetchTaskTypes(), adminTaskTypeTableArea);
        setTaskTypes(taskTypes);
        setSortedTaskTypes(taskTypes);
    }

     const goToTaskTypeCreateScreen = async () => {
        let isNewTaskType = false;
        const result = await GetNewTaskTypeSettings(0);
        if (result?.status === 200) {
            isNewTaskType = result.data;
        }
        const pathname = isNewTaskType
            ? `/admin/task-types/task-type/0`
            : `/admin/task-types/dynamic-fields/0`;
        history.push({ pathname });
    };

    function getSelectedName(taskTypeId) {
        if (selectedTaskTypes.length === 0) return '';
        return taskTypes.filter((taskType) => taskType.Id === taskTypeId)[0].Type;
    }

    if (promiseInProgress) {
        return (
            <div className="pulse-loader">
                <PulseLoader />
            </div>
        );
    }

    if (alertError.showAlert) return <ErrorAlert {...errorAlertProps} />;

    return (
        <div className="admin-task-types-table-wrapper">
            <div className="admin-panel-table-container">
                <div className="table-control-container">
                    <TriyoButton>
                        <PrimaryButton
                            onClick={goToTaskTypeCreateScreen}
                            disabled={!canCreate}
                            text="Create New Task Type" />
                        <SecondaryButton
                            onClick={() => setShowAdminActionsPopup(true)}
                            disabled={!canArchive || selectedTaskTypes.length === 0}
                            text="Archive" />
                    </TriyoButton>
                    <Pagination {...paginationProps} />
                </div>
                <div className="table-main">
                    <table className="table-element">
                        <TaskTypesTableHeader {...headerProps} />
                        <tbody>
                            {sortedTaskTypes.map((taskType, idx) => (
                                <TaskTypesTableRow key={taskType?.Id} taskType={taskType} {...rowProps} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {showAdminActionsPopup && <AdminActionsPopup {...adminActionsPopupProps} />}
        </div>
    );
};

const mapState = createStructuredSelector({ user: selectUser });

export default connect(mapState)(TaskTypesTable);